/**
 * Display-only arithmetic on decimal strings.
 *
 * The backend is the sole authority for computed monetary totals (AGENTS.md
 * §3); what this file is for is the column footers and live previews a screen
 * renders from figures the backend already sent. A total that GATES anything —
 * the opening batch's `difference`, an invoice's payable amount — always comes
 * from the API, never from here.
 *
 * The sums are exact: each string is scaled to four decimal places into a
 * BigInt (NUMERIC(19,4) scale, ADR-0012), added as integers, and scaled back —
 * no IEEE double ever touches an amount, the same rule `formatMoney` follows.
 * `parseFloat` on a money string is the bug this file exists to prevent.
 */
const SCALE = 10_000n

/**
 * A non-negative decimal literal: digits, an optional dot and up to four
 * fraction digits — no sign, no exponent, no trailing junk. The shape a
 * NUMERIC(19,4) column round-trips through a form field.
 */
export const DECIMAL_STRING_PATTERN = /^\d{1,15}(\.\d{1,4})?$/

/** A percentage literal: 0..100 with up to two fraction digits. */
export const PERCENT_STRING_PATTERN = /^(\d{1,2}|100)(\.\d{1,2})?$/

/**
 * One validated decimal string as a JSON number, for the few request bodies
 * whose generated contract still declares the field a number
 * (`StoreProductRequest.sales_price` and friends).
 *
 * This is a WIRE conversion, not arithmetic: the schema has already pinned the
 * string to DECIMAL_STRING_PATTERN, so `Number` reads a plain literal — there
 * is none of parseFloat's leniency that made "12abc" parse as 12. It never
 * touches display or comparison paths; those stay in this file's exact
 * BigInt helpers.
 */
export function decimalStringToNumber(value: string): number {
  return Number(value)
}

export function sumDecimalStrings(values: Array<string | null | undefined>): string {
  let total = 0n
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue
    total += scaledFromDecimal(value)
  }
  return decimalFromScaled(total)
}

/** The magnitude of a possibly-negative decimal string, for "short by / over by". */
export function absoluteDecimalString(value: string): string {
  return value.startsWith('-') ? value.slice(1) : value
}

/**
 * The sign of a decimal string: -1, 0 or 1.
 *
 * For deciding which way a figure went — over or under, above or below — where
 * `parseFloat` would be the easy answer and the wrong one: a NUMERIC(19,4) can
 * carry more significant digits than a double holds, so a large amount that is
 * genuinely non-zero can round to something that compares equal to zero. Read
 * as text, `-0.0001` and `10000000000000000.0001` both answer correctly.
 *
 * An unparseable string answers 0 — neither direction — rather than inventing
 * one from NaN.
 */
export function decimalSign(value: string): -1 | 0 | 1 {
  if (!/^-?\d*\.?\d*$/.test(value) || /^-?\.?$/.test(value)) return 0
  if (/^-?0*\.?0*$/.test(value)) return 0

  return value.startsWith('-') ? -1 : 1
}

/** The exact product of two decimal strings, rounded half away from zero to 4 dp. */
export function multiplyDecimalStrings(a: string, b: string): string {
  const left = parseDecimalParts(a)
  const right = parseDecimalParts(b)
  return decimalFromScaled(rescaleRound(left.digits * right.digits, left.scale + right.scale, 4))
}

/**
 * The exact product of two decimal strings, rounded once, half away from zero,
 * to `places` decimals: `'3' × '290.32'` at 2 places is `'870.96'`.
 */
export function multiplyDecimalStringsTo(a: string, b: string, places: number): string {
  const left = parseDecimalParts(a)
  const right = parseDecimalParts(b)
  const rounded = rescaleRound(left.digits * right.digits, left.scale + right.scale, places)
  const negative = rounded < 0n
  const digits = (negative ? -rounded : rounded).toString().padStart(places + 1, '0')
  const whole = digits.slice(0, digits.length - places)
  const fraction = places > 0 ? `.${digits.slice(-places)}` : ''
  return `${negative ? '-' : ''}${whole}${fraction}`
}

/** Exact percentage of a product, rounded half-up once to four decimal places. */
export function percentageOfProductDecimalStrings(
  a: string,
  b: string,
  percentage: string,
): string {
  const left = parseDecimalParts(a)
  const right = parseDecimalParts(b)
  const rate = parseDecimalParts(percentage)
  const denominator = 100n * 10n ** BigInt(left.scale + right.scale + rate.scale)
  const numerator = left.digits * right.digits * rate.digits * SCALE
  return decimalFromScaled(divideRoundHalfUp(numerator, denominator))
}

/** Exact percentage of one decimal string, rounded half-up once to four places. */
export function percentageDecimalString(amount: string, percentage: string): string {
  return percentageOfProductDecimalStrings(amount, '1', percentage)
}

/**
 * The quotient of two decimal strings, rounded half away from zero to
 * `places` decimals. Division by zero answers null rather than a number.
 */
export function divideDecimalStrings(a: string, b: string, places = 4): string | null {
  const left = parseDecimalParts(a)
  const right = parseDecimalParts(b)
  if (right.digits === 0n) return null
  const numerator = left.digits * 10n ** BigInt(right.scale + places)
  const denominator = right.digits * 10n ** BigInt(left.scale)
  const negative = numerator < 0n !== denominator < 0n
  const quotient = divideRoundHalfUp(
    numerator < 0n ? -numerator : numerator,
    denominator < 0n ? -denominator : denominator,
  )
  const digits = quotient.toString().padStart(places + 1, '0')
  const whole = digits.slice(0, digits.length - places)
  const fraction = places > 0 ? `.${digits.slice(-places)}` : ''
  return `${negative && quotient !== 0n ? '-' : ''}${whole}${fraction}`
}

/** The exact difference of two decimal strings at 4 dp. */
export function subtractDecimalStrings(a: string, b: string): string {
  const left = parseDecimalParts(a)
  const right = parseDecimalParts(b)
  const scale = Math.max(left.scale, right.scale)
  return decimalFromScaled(
    rescaleRound(alignScale(left, scale) - alignScale(right, scale), scale, 4),
  )
}

/**
 * The order of two decimal strings: -1 when a < b, 0 when equal, 1 when a > b.
 * Exact — no rounding, so a guard can never flip on a double's last bit.
 */
export function compareDecimalStrings(a: string, b: string): -1 | 0 | 1 {
  const left = parseDecimalParts(a)
  const right = parseDecimalParts(b)
  const scale = Math.max(left.scale, right.scale)
  const aScaled = alignScale(left, scale)
  const bScaled = alignScale(right, scale)
  return aScaled < bScaled ? -1 : aScaled > bScaled ? 1 : 0
}

/**
 * A stored decimal as a number input shows it: `'18500.0000'` → `'18500'`.
 * Only trailing fraction zeros go; no grouping, so the input can still parse it.
 */
export function inputDecimal(value: string): string {
  return value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value
}

/** A decimal string as its exact signed integer of digits and their scale. */
type DecimalParts = { digits: bigint; scale: number }

/**
 * Parse into digits and scale (`value === digits / 10**scale`). Unparseable
 * input reads as zero — the same tolerance `decimalSign` applies.
 */
function parseDecimalParts(value: string): DecimalParts {
  if (!/^-?\d*\.?\d*$/.test(value) || /^-?\.?$/.test(value)) return { digits: 0n, scale: 0 }
  const negative = value.startsWith('-')
  const unsigned = negative ? value.slice(1) : value
  const [whole = '', fraction = ''] = unsigned.split('.')
  const digits = BigInt(`${whole === '' ? '0' : whole}${fraction}`)
  return { digits: negative ? -digits : digits, scale: fraction.length }
}

/** Rescale to exactly `target` fraction digits, rounding half away from zero. */
function rescaleRound(digits: bigint, scale: number, target: number): bigint {
  if (scale < target) return digits * 10n ** BigInt(target - scale)
  if (scale > target) {
    const drop = 10n ** BigInt(scale - target)
    const negative = digits < 0n
    const magnitude = negative ? -digits : digits
    const rounded = (magnitude + drop / 2n) / drop
    return negative ? -rounded : rounded
  }
  return digits
}

function divideRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n
  const magnitude = negative ? -numerator : numerator
  const rounded = (magnitude + denominator / 2n) / denominator
  return negative ? -rounded : rounded
}

function alignScale(parts: DecimalParts, target: number): bigint {
  return parts.digits * 10n ** BigInt(target - parts.scale)
}

function scaledFromDecimal(value: string): bigint {
  const negative = value.startsWith('-')
  const unsigned = negative ? value.slice(1) : value
  const [whole, fraction = ''] = unsigned.split('.')
  const digits = `${whole}${`${fraction}0000`.slice(0, 4)}`
  const scaled = BigInt(digits === '' ? '0' : digits)
  return negative ? -scaled : scaled
}

function decimalFromScaled(scaled: bigint): string {
  const negative = scaled < 0n
  const abs = negative ? -scaled : scaled
  const whole = abs / SCALE
  const fraction = (abs % SCALE).toString().padStart(4, '0')
  return `${negative ? '-' : ''}${whole}.${fraction}`
}
